import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  RiAddLine,
  RiCheckLine,
  RiDeleteBinLine,
  RiDraftLine,
  RiErrorWarningLine,
  RiLoader4Line,
  RiMessage3Line,
  RiPencilLine,
  RiTimerLine,
} from "@remixicon/react";
import { announcementStats, deleteAnnouncement, listAnnouncements } from "../../services/announcements";
import { useAuth } from "../../contexts/AuthContext";
import { logUserActivity } from "../../utils/activityLogger";
import { showingOfLabel } from "../../utils/kitPresentation";
import { pageSlice } from "../../utils/tutorPresentation";
import {
  AUDIENCE_OPTIONS,
  CHANNEL_LABEL,
  PAGE_SIZE,
  STATUS,
  STATUS_OPTIONS,
  audienceCell,
  emptyState,
  filterRows,
  openedLabel,
  senderLine,
  sortRows,
  whenCell,
} from "../../utils/announcementPresentation";
import { srOnly, tokens } from "../../utils/figmaTokens";
import { TableCard } from "../../components/kit/SectionCard";
import { StateAction } from "../../components/kit/States";
import { EyeButton, RowAction, SkeletonRows, SortHead, TableMessageRow, TwoLineCell } from "../../components/kit/TableParts";
import Pager from "../../components/kit/Pager";
import SearchField from "../../components/kit/SearchField";
import ToolbarMenu from "../../components/kit/ToolbarMenu";

// Notification history (spec 044 D39–D48), drawn from Figma's History screen (11326:89654):
// the head with New notification, the search and the Status and Audience menus, the table and
// its pager. The page reads the last 90 days once (listAnnouncements) and searches, filters,
// sorts and pages in the browser. A row, its pencil and its eye open the announcement's own
// page (run 2); the bin deletes a draft or a scheduled one after a confirm.

const COLUMNS = 7;

// The status chip's left icon (Chip 9999:1898, Small, Soft): Figma draws timer-line for
// Scheduled, check-line for Sent and draft-line for Draft; Sending and Failed take the same slot.
const STATUS_ICON = {
  draft: RiDraftLine,
  scheduled: RiTimerLine,
  sending: RiLoader4Line,
  sent: RiCheckLine,
  failed: RiErrorWarningLine,
};
// The chip's 10 px padding and a 6 px gap between the 16 px icon and the label.
const statusChipSx = { "& .MuiChip-icon": { ml: "10px", mr: "-4px", color: "inherit" } };

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // The New notification page hands back one line and its colour ({ notice: "Sent to 310
  // students", severity }, D45); shown once, and dropped from the history entry so a reload or
  // Back does not show it again.
  const [notice, setNotice] = useState(location.state?.notice ? location.state : null);
  useEffect(() => {
    if (location.state?.notice) navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);
  const [rows, setRows] = useState([]);
  const [now, setNow] = useState(() => new Date()); // the clock the rows were read at: the When column's "ago"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(""); // the error block's line; "" when the list loaded

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [role, setRole] = useState("all");
  const [sortField, setSortField] = useState("when");
  const [sortDirection, setSortDirection] = useState("desc");
  const [page, setPage] = useState(0);
  // id -> opens, from announcementStats; null where the call failed, so it is not asked again.
  const [opened, setOpened] = useState({});

  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const readAt = new Date();
      setRows(await listAnnouncements(readAt));
      setNow(readAt);
      setError("");
    } catch (e) {
      console.error("Error loading announcements:", e);
      // Error State 10618:2251: always say whether anything was changed.
      setError("Nothing has been changed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(
    () => sortRows(filterRows(rows, { search, status, role }), sortField, sortDirection, { opened }),
    [rows, search, status, role, sortField, sortDirection, opened]
  );
  const slice = pageSlice(visible, page, PAGE_SIZE);

  // The Opened column (044 D46): announcementStats for the sent rows on this page that have no
  // number yet — for every listed sent row while Opened is the sort, or the order would follow
  // whichever pages happened to load. A failed call leaves "—" and does not fail the page.
  const statsKey = (sortField === "opened" ? visible : slice.rows)
    .filter((r) => r.status === "sent" && !(r.id in opened))
    .map((r) => r.id)
    .join(",");
  useEffect(() => {
    if (!statsKey) return undefined;
    const ids = statsKey.split(",");
    let cancelled = false;
    announcementStats({ ids })
      .then((data) => {
        if (!cancelled) setOpened((prev) => ({ ...prev, ...Object.fromEntries(ids.map((id) => [id, data?.opened?.[id] ?? null])) }));
      })
      .catch((e) => {
        console.error("Error loading announcement stats:", e);
        if (!cancelled) setOpened((prev) => ({ ...prev, ...Object.fromEntries(ids.map((id) => [id, null])) }));
      });
    return () => {
      cancelled = true;
    };
  }, [statsKey]);

  // A new search, filter or sort starts again on page 1 (P58).
  const changeSearch = (value) => {
    setSearch(value);
    setPage(0);
  };
  const changeStatus = (value) => {
    setStatus(value);
    setPage(0);
  };
  const changeRole = (value) => {
    setRole(value);
    setPage(0);
  };
  const toggleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else {
      setSortField(field);
      setSortDirection(field === "when" ? "desc" : "asc");
    }
    setPage(0);
  };

  const openDelete = (row) => {
    setDeleteError("");
    setDeleteRow(row);
  };

  // 044 D45: only a draft or a scheduled announcement can go; the server refuses the rest.
  // The log row is best effort: an accountant's write is denied by the rules, and
  // logUserActivity swallows that, so the delete never looks failed for it.
  const handleDelete = async () => {
    if (!deleteRow) return;
    setDeleting(true);
    try {
      await deleteAnnouncement({ id: deleteRow.id });
      await logUserActivity({
        userId: user?.uid || user?.id || "unknown",
        userName: user?.name || user?.displayName || "Unknown User",
        userEmail: user?.email || "unknown@example.com",
        action: "DELETE_ANNOUNCEMENT",
        entityType: "announcement",
        entityId: deleteRow.id,
        entityName: deleteRow.title || deleteRow.id,
        before: { status: deleteRow.status, title: deleteRow.title || "", audience: deleteRow.audience || null },
        after: {},
        description: `${user?.name || "User"} deleted ${STATUS[deleteRow.status]?.label.toLowerCase() || ""} announcement: ${deleteRow.title || deleteRow.id}`,
      });
      setDeleteRow(null);
      await load();
    } catch (e) {
      console.error("Error deleting announcement:", e);
      setDeleteError(e.message || "Could not delete the notification. Nothing has been changed.");
    } finally {
      setDeleting(false);
    }
  };

  const failed = Boolean(error) && !loading;
  const empty = emptyState({ search, status, role });
  const clearAll = () => {
    if (empty.clear === "search") changeSearch("");
    else {
      setStatus("all");
      setRole("all");
      setPage(0);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {/* The card's head carries the visible title, so the page's h1 is for screen readers only (P59). */}
      <Typography component="h1" sx={srOnly}>
        Notifications
      </Typography>

      <TableCard
        title="Notification history"
        subtitle="Every notification sent, scheduled or failed in the last 90 days"
        action={
          <Button size="large" variant="contained" component={RouterLink} to="/notifications/new" startIcon={<RiAddLine size={20} />}>
            New notification
          </Button>
        }
        toolbar={
          <>
            <SearchField value={search} onChange={changeSearch} placeholder="Search…" />
            <ToolbarMenu id="notifications-status" label="Status" value={status} options={STATUS_OPTIONS} onChange={changeStatus} />
            <ToolbarMenu id="notifications-audience" label="Audience" value={role} options={AUDIENCE_OPTIONS} onChange={changeRole} />
          </>
        }
        footer={
          // While the list loads or after it failed, the label is empty and both buttons are off.
          <Pager
            label={loading || failed ? "" : showingOfLabel(slice.page, slice.rows.length, PAGE_SIZE, visible.length)}
            page={loading || failed ? 0 : slice.page}
            hasMore={!loading && !failed && slice.hasMore}
            onPage={setPage}
          />
        }
      >
        <TableContainer>
          <Table aria-busy={loading}>
            <TableHead>
              <TableRow>
                <SortHead label="Notification" active={sortField === "title"} direction={sortDirection} onSort={() => toggleSort("title")} />
                <SortHead label="Audience" active={sortField === "audience"} direction={sortDirection} onSort={() => toggleSort("audience")} />
                <SortHead label="When" active={sortField === "when"} direction={sortDirection} onSort={() => toggleSort("when")} />
                <TableCell>Channel</TableCell>
                <SortHead label="Status" active={sortField === "status"} direction={sortDirection} onSort={() => toggleSort("status")} />
                <SortHead label="Opened" active={sortField === "opened"} direction={sortDirection} onSort={() => toggleSort("opened")} />
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <SkeletonRows columns={COLUMNS} />
              ) : failed || slice.rows.length === 0 ? (
                <TableMessageRow
                  colSpan={COLUMNS}
                  error={failed ? "Could not load notifications" : ""}
                  errorMessage={error}
                  onRetry={load}
                  emptyText={empty.title}
                  emptyMessage={empty.message}
                  emptyActions={
                    empty.clear ? (
                      <StateAction outlined onClick={clearAll}>
                        {empty.clear === "search" ? "Clear search" : "Clear filters"}
                      </StateAction>
                    ) : (
                      <StateAction onClick={() => navigate("/notifications/new")}>New notification</StateAction>
                    )
                  }
                />
              ) : (
                slice.rows.map((a) => {
                  const chip = STATUS[a.status] || { label: a.status || "—", color: "default" };
                  const Icon = STATUS_ICON[a.status];
                  const editable = a.status === "draft" || a.status === "scheduled";
                  const [audience, people] = audienceCell(a);
                  const [when, whenSub] = whenCell(a, now);
                  const to = `/notifications/${a.id}`;
                  const name = a.title || "notification";
                  return (
                    <TableRow key={a.id} hover onClick={() => navigate(to)} sx={{ cursor: "pointer" }}>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
                          {a.title || "Untitled"}
                        </Typography>
                        <Typography variant="labelMedium" component="div" noWrap sx={{ color: tokens.text.tertiary }}>
                          {senderLine(a)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TwoLineCell top={audience} bottom={people} />
                      </TableCell>
                      <TableCell>
                        <TwoLineCell top={when} bottom={whenSub} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px", color: tokens.text.primary }}>
                          <RiMessage3Line size={16} color={tokens.icon.default} aria-hidden />
                          {CHANNEL_LABEL}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={chip.label} color={chip.color} icon={Icon ? <Icon size={16} aria-hidden /> : undefined} sx={statusChipSx} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{openedLabel(a, opened[a.id])}</TableCell>
                      {/* The row opens the announcement; its buttons must not open it a second time. */}
                      <TableCell onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: "nowrap" }}>
                        {editable ? (
                          <>
                            <RowAction Icon={RiPencilLine} label={`Edit ${name}`} onClick={() => navigate(to)} />
                            <RowAction Icon={RiDeleteBinLine} label={`Delete ${name}`} onClick={() => openDelete(a)} />
                          </>
                        ) : (
                          <EyeButton to={to} label={`Open ${name}`} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      {/* Admin · Confirm Dialog 10708:8947, as Plans and Staff open it. */}
      <Dialog open={Boolean(deleteRow)} onClose={() => !deleting && setDeleteRow(null)} fullWidth maxWidth="sm">
        <DialogTitle>Delete this notification?</DialogTitle>
        <DialogContent dividers>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            {deleteRow &&
              `"${deleteRow.title || "Untitled"}" (${STATUS[deleteRow.status]?.label.toLowerCase() || deleteRow.status}) will be removed${
                deleteRow.status === "scheduled" ? " and will not be sent" : ""
              }. This cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice(null)}>
        <Alert severity={notice?.severity || "success"} onClose={() => setNotice(null)}>
          {notice?.notice}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Notifications;
